import { Stack } from '@mui/material';
import { Text } from '../atoms/Text/Text';
import Button from '../Buttons/Button/Button';
import { AnalyticEventsEnum, trackEvent } from '../../Analytics';
import { useTypedDispatch } from '../../redux/hooks';
import { usePointLocation } from '../../hooks/hook.location';
import * as Yup from 'yup';
import s from './LocationModal.module.scss';
import { isActiveCountry, LocationFullType } from '../Location/config';
import Form from '../Form/Form';
import { InputLocation } from '../Inputs/InputLocation/InputLocation';
import { useForm } from 'react-hook-form';
import { useSumbitFormCallback } from '../Form/submit.hook';
import { validators } from '../../utils/validators';
import { yupResolver } from '@hookform/resolvers/yup';
import { EmailForm } from './EmailForm';
import { useState } from 'react';
import { locationActionCreators } from '../../redux/slices/location/actionCreators';

export type LocationFormValues = { location: LocationFullType };

const validationOrdered = {
  location: validators.location,
};

const validationSchema: Yup.SchemaOf<Partial<LocationFormValues>> =
  Yup.object().shape(validationOrdered);

type FieldNameType = keyof typeof validationOrdered;

export const LocationForm = ({
  submitModal,
  placement,
}: {
  submitModal: () => void;
  placement: 'welcome' | 'options';
}) => {
  const dispatch = useTypedDispatch();
  const pointLocation = usePointLocation();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isSubmitted, isSubmitSuccessful },
    watch,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      location: placement === 'welcome' ? pointLocation : null,
    } as Partial<LocationFormValues>,
  });

  const location = watch('location');

  const [isSubscribed, setIsSubscribed] = useState(false);

  const submitCallback = async ({ location }: LocationFormValues) => {
    if (isActiveCountry(location.country)) {
      await dispatch(locationActionCreators.selectLocation(location));
      submitModal();
    } else {
      // fill be switched to another state based on "isSubmitted"
    }

    await trackEvent(AnalyticEventsEnum.LOCATION__CITY_SUBMITTED, {
      location: location.description,
    });
  };

  const { onSubmit } = useSumbitFormCallback<LocationFormValues>({
    submitCallback,
    handleSubmit,
    reset,
    trigger,
    isSubmitted,
    setValue,
    hideToast: true,
    trackSubmitError: true,
  });

  const isLocated = isSubmitSuccessful;

  return (
    <Stack marginTop="15px" marginBottom="5px">
      {!isLocated && (
        <p
          style={{
            marginBottom: '10px',
            fontSize: '22px',
            fontFamily: 'Montserrat',
            textAlign: 'center',
          }}
        >
          or
        </p>
      )}

      <Stack
        spacing={1}
        style={{
          width: '100%',
          borderRadius: '15px',
          padding: '10px',
          alignSelf: 'center',
          boxShadow: '0 0 3px rgba(255, 255, 255, 0.1)',
          background: 'var(--color-card)',
        }}
      >
        <Text
          fontSize="20px"
          fontWeight={500}
          color={
            isSubscribed
              ? '#66c468'
              : isLocated
              ? 'var(--ion-color-secondary)'
              : 'var(--color-main-high)'
          }
          extraStyle={{ fontFamily: 'Montserrat' }}
        >
          {isSubscribed
            ? 'Subscribed!'
            : isLocated
            ? `Get early access!`
            : placement === 'welcome'
            ? 'Type in event location'
            : 'Type in area'}
        </Text>

        {isLocated &&
          (isSubscribed ? (
            <Text>
              We'll let you know as soon as service becomes available in{' '}
              <i>{location?.country}</i>
            </Text>
          ) : (
            <Text>
              Should we notify you when this platform becomes available in{' '}
              <i>{location?.country}</i>?
            </Text>
          ))}

        {!isLocated ? (
          <Form hidePrompt onSubmit={onSubmit}>
            <Stack direction="row" alignItems="stretch" spacing={1}>
              <InputLocation
                fontSize="16px"
                _name={'location' satisfies FieldNameType}
                _control={control}
                _register={register}
                _errorText={errors.location?.message}
                _setValue={setValue}
              />

              <Button
                type="submit"
                className={s.Explore__Button}
                skipCentralizer
                width="100px"
                text="Explore"
                color="primary"
                fill="solid"
              />
            </Stack>
          </Form>
        ) : (
          <EmailForm
            location={location}
            isSubscribed={isSubscribed}
            setIsSubscribed={() => setIsSubscribed(true)}
          />
        )}
      </Stack>
    </Stack>
  );
};
